import React, {Component} from 'react';
import {
    ArrayInput,
    BooleanInput,
    Confirm,
    FileInput,
    FormDataConsumer,
    LongTextInput,
    NumberInput,
    SelectArrayInput,
    SelectInput,
    SimpleForm,
    SimpleFormIterator,
    TextInput,
    translate
} from 'react-admin';
import PropTypes from 'prop-types';
import {withStyles} from '@material-ui/core/styles';
import {connect} from 'react-redux';
import {change, submit} from 'redux-form';
import Button from '@material-ui/core/Button';
import {withRouter} from 'react-router-dom';
import RichTextInput from 'ra-input-rich-text';

import {isRequired} from '~utils/inputValidations';
import FieldGroup from '~components/FieldGroup';
import FileLinkField from '~components/FileLinkField';
import GalleryItemsInput from '~components/GalleryItemsInput';
import {LeaveGuardContext} from '~components/Breadcrumbs';
import {IirsaCountriesService} from '~config/services/iirsa_countries.service';
import {TerritoriesService} from '~config/services/territories.service';
import {CodesService} from '~config/services/codes.service';
import {EtapasService} from '~config/services/etapas.service';
import {ScopesService} from '~config/services/scopes.service';
import {TermsService} from '~config/services/terms.service';
import {FuentesService} from '~config/services/fuentes.service';
import {FinanciadoresEstudiosService} from '~config/services/financiadores_estudios.service';
import {TiposFinanciamientoService} from '~config/services/tipos_financiamiento.service';
import {TypesService} from '~config/services/types.service';
import {ThemesService} from '~config/services/themes.service';
import {PriorizacionesService} from '~config/services/priorizaciones.service';

import DynamicMap from '../components/DynamicMap';
import './styles.scss';

import styles from './styles';

const VolverButton = props => (<>
        <Button style={styles.volverButton} onClick={props.onVolverClick}>
            <svg style={styles.chevron} viewBox="0 0 32 32" class="icon icon-chevron-left">
                <path
                    fill="#3f51b5"
                    d="M14.19 16.005l7.869 7.868-2.129 2.129-9.996-9.997L19.937 6.002l2.127 2.129z"
                />
            </svg>
            Volver
        </Button>
        <Confirm
            isOpen={props.showConfirm}
            title="¿Desea salir del formulario?"
            content="Los cambios realizados se perderán"
            confirm="Si"
            confirmColor="primary"
            cancel="Cancelar"
            onConfirm={props.onVolverConfirm}
            onClose={props.onVolverCancel}
        />
    </>);

const toolbarOptions = [['bold', 'italic', 'underline', 'link'], [{list: 'bullet'}]];

const byName = (a, b) => (a.name > b.name ? 1 : -1);

// Subtema choices labelled with their Tema, so one multi-select covers both levels.
const subthemeChoices = themes => themes
    .slice()
    .sort(byName)
    .flatMap(theme => (theme.subthemes || [])
        .slice()
        .sort(byName)
        .map(subtheme => ({id: subtheme.id, name: `${theme.name} › ${subtheme.name}`})));

// Only offer Territorios of the selected Países (the API rejects any other).
const territoryChoices = (territories, countries) => (countries && countries.length
    ? territories.filter(t => (t.countries || []).some(c => countries.includes(c)))
    : territories);

// The map center is only needed when there is a map to center.
const requiredWithFile = (fileSource, msg) => (value, allValues) =>
    allValues[fileSource] && (value === null || value === undefined || value === '') ? msg : undefined;

const REQUIRED_LISTS = {
    countries: 'Debe seleccionar al menos un país',
    territories: 'Debe seleccionar al menos un territorio',
    subthemes: 'Debe seleccionar al menos un subtema',
    scopes: 'Debe seleccionar al menos un ámbito'
};


class AccionForm extends Component {
    state = {
        countries: [],
        territories: [],
        codes: [],
        subthemesChoices: [],
        etapasChoices: [],
        scopesChoices: [],
        typesChoices: [],
        termsChoices: [],
        sourcesChoices: [],
        financiadoresEstudiosChoices: [],
        tiposFinanciamientoChoices: [],
        priorizacionesChoices: [],
        showConfirm: false
    };

    firstForm = null;
    hasChanged = false;

    constructor() {
        super();

        // Load a catalog into state[key], sorted by name; paginated responses carry `page`.
        // A failed request leaves the choices empty instead of crashing the form.
        const load = (request, key, transform = data => data.slice().sort(byName)) =>
            request.then(r => {
                const data = r.data && (r.data.page || r.data);
                if (Array.isArray(data)) {
                    this.setState({[key]: transform(data)});
                }
            });

        load(new IirsaCountriesService().get_la(), 'countries');
        load(new TerritoriesService().get(), 'territories');
        load(new CodesService().get(), 'codes', data => data);
        load(new ThemesService().get(), 'subthemesChoices', subthemeChoices);
        load(new EtapasService().get(), 'etapasChoices');
        load(new ScopesService().get(), 'scopesChoices');
        load(new TypesService().get(), 'typesChoices');
        load(new TermsService().get(), 'termsChoices');
        load(new FuentesService().get(), 'sourcesChoices');
        load(new FinanciadoresEstudiosService().get(), 'financiadoresEstudiosChoices');
        load(new TiposFinanciamientoService().get(), 'tiposFinanciamientoChoices');
        load(new PriorizacionesService().get(), 'priorizacionesChoices');
    }

    // Draft mode relaxes every required-field validator: a draft may be saved
    // incomplete. Provided values are still validated by the inputs/serializer.
    get isDraft() {
        return !!this.props.draft;
    }

    // `isRequired` normally; no validator in draft mode.
    get req() {
        return this.isDraft ? undefined : isRequired;
    }

    // Map center required only when its file is set; no validator in draft mode.
    reqCenter(fileSource, msg) {
        return this.isDraft ? [] : [requiredWithFile(fileSource, msg)];
    }

    // code validators: [isRequired, codeIsUnique] normally; none in draft mode.
    get reqCode() {
        return this.isDraft ? undefined : [isRequired, this.codeIsUnique];
    }

    codeIsUnique = (value, allValues, {translate}) => {
        if (this.state.codes?.indexOf(value) > -1) {
            return 'Este código ya se encuentra en uso';
        }
        return undefined;
    }

    validateProjectForm = values => {
        const errors = {};
        if (!this.isDraft) {
            Object.keys(REQUIRED_LISTS).forEach(source => {
                if (!values[source] || values[source].length == 0) {
                    errors[source] = [REQUIRED_LISTS[source]];
                }
            });
        }
        if (values['slug'] && this.state.slugs?.indexOf(values.slug) > -1) {
            errors.slug = 'Este slug ya se encuentra en uso';
        }
        if (values['image'] && values['image'].rawFile && values['image'].rawFile.size > 15000000) {
            errors.image = 'El tamaño de la imagen no puede ser mayor a 15MB';
        }
        return errors;
    };

    get listPath() {
        return this.isDraft ? '/draft-actions' : '/actions';
    }

    static contextType = LeaveGuardContext;

    componentDidMount() {
        this.context.current = this.requestLeave;
    }

    componentWillUnmount() {
        if (this.context.current === this.requestLeave) {
            this.context.current = null;
        }
    }

    // Shared by Volver and the breadcrumb links: confirm first if the form has changes.
    requestLeave = path => {
        if (this.hasChanged) {
            this.setState({showConfirm: true, pendingPath: path});
        } else {
            this.props.history.push(path);
        }
    };

    onVolverClick = () => this.requestLeave(this.listPath);

    onVolverConfirm = () => {
        this.props.history.push(this.state.pendingPath);
    };

    onVolverCancel = () => {
        this.setState({showConfirm: false});
    };

    isCreateForm() {
        return this.props.location?.pathname.endsWith('/create');
    }

    render() {
        return (<>
                <VolverButton
                    showConfirm={this.state.showConfirm}
                    onVolverClick={this.onVolverClick}
                    onVolverConfirm={this.onVolverConfirm}
                    onVolverCancel={this.onVolverCancel}
                />
                <SimpleForm validate={this.validateProjectForm} {...this.props}>
                    {this.children}

                    <FormDataConsumer>
                        {data => {
                            if (!this.firstForm) {
                                this.firstForm = data;
                            } else {
                                if (!(JSON.stringify(data) === JSON.stringify(this.firstForm))) {
                                    this.hasChanged = true;
                                }
                            }

                            if (data && data.formData && data.formData.end_date) {
                                data.formData.end_date = data.formData.end_date.substring(0, 7);
                            }
                        }}
                    </FormDataConsumer>

                    <FieldGroup
                        label="fieldGroups.projectInformation"
                        formClassName={this.props.classes.borderBottom}
                        titleColumn={false}
                        flex={false}
                    >
                        <TextInput source="name" validate={this.req} className={'colInput'}/>
                        {(this.isCreateForm() || this.isDraft) ? <TextInput source="code" validate={this.reqCode}
                                                          className={'colInput'}/> :
                            <TextInput source="code" className={'colInput'} disabled/>}
                    </FieldGroup>

                    <FieldGroup
                        label="fieldGroups.scope"
                        formClassName={this.props.classes.borderBottom}
                        titleColumn={false}
                        flex={false}
                    >
                        <div className={'colInputContainer'}>
                            <label style={styles.inputLabel}>Objetivos (*)</label>
                            <RichTextInput source="goal" toolbar={toolbarOptions} validate={this.req}/>
                        </div>
                        <div className={'colInputContainer'}>
                            <label style={styles.inputLabel}>Descripción (*)</label>
                            <RichTextInput source="description" toolbar={toolbarOptions} validate={this.req}/>
                        </div>
                    </FieldGroup>

                    <FieldGroup
                        label="fieldGroups.ilat"
                        formClassName={this.props.classes.borderBottom}
                        titleColumn={false}
                        flex={false}
                    >
                        <BooleanInput source="is_portfolio" className={'colInputContainer'}/>
                        <BooleanInput source="accompaniment" className={'colInputContainer'}/>
                        <BooleanInput source="is_bid" className={'colInputContainer'}/>
                        <BooleanInput source="is_caf" className={'colInputContainer'}/>
                        <BooleanInput source="is_fonplata" className={'colInputContainer'}/>
                        <BooleanInput source="is_anchor" className={'colInputContainer'}/>
                    </FieldGroup>

                    <FieldGroup
                        label="fieldGroups.general"
                        formClassName={this.props.classes.borderBottom}
                        titleColumn={false}
                        flex={false}
                    >
                        <SelectArrayInput
                            source="countries"
                            choices={this.state.countries}
                            className={'colInput'}
                        />

                        <FormDataConsumer>
                            {({formData}) => (<SelectArrayInput
                                source="territories"
                                label="resources.actions.fields.territories"
                                choices={territoryChoices(this.state.territories, formData?.countries)}
                                className={'colInput'}
                            />)}
                        </FormDataConsumer>

                        <SelectArrayInput
                            source="subthemes"
                            choices={this.state.subthemesChoices}
                            className={'colInput'}
                        />

                        <SelectArrayInput
                            source="types"
                            choices={this.state.typesChoices}
                            className={'colInput'}
                        />

                        <SelectArrayInput
                            source="scopes"
                            choices={this.state.scopesChoices}
                            className={'colInput'}
                        />

                        <ArrayInput source="responsibles">
                            <SimpleFormIterator>
                                <TextInput source="name" label="Responsable" validate={isRequired}/>
                            </SimpleFormIterator>
                        </ArrayInput>
                    </FieldGroup>

                    <FieldGroup
                        label="fieldGroups.projectStatus"
                        formClassName={this.props.classes.borderBottom}
                        titleColumn={false}
                        flex={false}
                    >
                        <SelectInput
                            source="stage"
                            choices={this.state.etapasChoices}
                            validate={this.req}
                            className={'colInput'}
                        />

                        <SelectInput
                            source="term"
                            choices={this.state.termsChoices}
                            allowEmpty
                            className={'colInput'}
                        />

                        <SelectInput
                            source="source"
                            choices={this.state.sourcesChoices}
                            validate={this.req}
                            allowEmpty
                            className={'colInput'}
                        />

                        <TextInput
                            source="end_date"
                            label="Fecha de finalización"
                            type="month"
                            className={'colInput'}
                        />

                        <SelectInput
                            source="prioritization"
                            label="Priorización"
                            choices={this.state.priorizacionesChoices}
                            allowEmpty
                            className={'colInput'}
                        />

                        <SelectInput
                            source="studies_funder"
                            choices={this.state.financiadoresEstudiosChoices}
                            className={'colInput'}
                        />

                        <div className={'colInputContainer'}>
                            <label style={styles.inputLabel}>Comentarios sobre el financiamiento de los estudios</label>
                            <RichTextInput source="studies_funding_comments" toolbar={toolbarOptions}/>
                        </div>

                    </FieldGroup>

                    <FieldGroup
                        label="fieldGroups.projectFunding"
                        formClassName={this.props.classes.borderBottom}
                        titleColumn={false}
                        flex={false}
                    >
                        <BooleanInput source="investment_unidentified"
                                      label="resources.actions.fields.investment_unidentified"/>
                        <FormDataConsumer>
                            {({formData}) => !formData.investment_unidentified && (
                                <NumberInput source="estimated_investment"
                                             label="resources.actions.fields.estimated_investment"
                                             validate={this.req}/>
                            )}
                        </FormDataConsumer>
                        <ArrayInput source="funding">
                            <SimpleFormIterator>
                                <SelectInput
                                    source="funder"
                                    choices={this.state.financiadoresEstudiosChoices}
                                    label="resources.actions.fields.funder"
                                />
                                <SelectInput
                                    source="funding_type"
                                    choices={this.state.tiposFinanciamientoChoices}
                                    label="resources.actions.fields.funding_type"
                                />
                            </SimpleFormIterator>
                        </ArrayInput>
                    </FieldGroup>

                    <FieldGroup
                        label="fieldGroups.dynamicMap"
                        formClassName={this.props.classes.borderBottom}
                        titleColumn={false}>

                        <FileInput label={'Mapa (.kml, .kmz, .geojson)'} source="kml"
                                   accept={[".kml", ".kmz", ".geojson"]}>
                            <FileLinkField/>
                        </FileInput>

                        <NumberInput source="dynamic_center_latitude" defaultValue={1}
                                     validate={this.reqCenter('kml', "Debe posicionar el mapa dinámico")}/>
                        <NumberInput source="dynamic_center_longitude" defaultValue={1}
                                     validate={this.reqCenter('kml', "Debe posicionar el mapa dinámico")}/>
                        <NumberInput source="dynamic_map_zoom" defaultValue={1}
                                     validate={this.reqCenter('kml', "Debe posicionar el mapa dinámico")}/>

                        <div style={{margin: 'auto'}}>
                            <FormDataConsumer>
                                {({formData}) => (formData && formData.kml) ? <>
                                    <h3>Ajustar mapa</h3>
                                    <DynamicMap
                                        kml={formData.kml}
                                        centerLat={formData.dynamic_center_latitude}
                                        centerLng={formData.dynamic_center_longitude}
                                        zoom={formData.dynamic_map_zoom}
                                        change={this.props.change}
                                        dynamic={true}
                                    />
                                </> : <></>}
                            </FormDataConsumer>
                        </div>
                    </FieldGroup>

                    <FieldGroup
                        label="fieldGroups.staticMap"
                        formClassName={this.props.classes.borderBottom}
                        titleColumn={false}>

                        <FileInput label={'Mapa simplificado (.kml, .kmz, .geojson)'} source="kml_simplified"
                                   accept={[".kml", ".kmz", ".geojson"]}>
                            <FileLinkField/>
                        </FileInput>

                        <NumberInput source="static_center_latitude" defaultValue={1}
                                     validate={this.reqCenter('kml_simplified', "Debe posicionar el mapa estático")}/>
                        <NumberInput source="static_center_longitude" defaultValue={1}
                                     validate={this.reqCenter('kml_simplified', "Debe posicionar el mapa estático")}/>
                        <NumberInput source="static_map_zoom" defaultValue={1}
                                     validate={this.reqCenter('kml_simplified', "Debe posicionar el mapa estático")}/>

                        <div style={{margin: 'auto'}}>
                            <FormDataConsumer>
                                {({formData}) => (formData && formData.kml_simplified) ? <>
                                    <h3>Ajustar mapa</h3>
                                    <DynamicMap
                                        kml={formData.kml_simplified}
                                        centerLat={formData.static_center_latitude}
                                        centerLng={formData.static_center_longitude}
                                        zoom={formData.static_map_zoom}
                                        change={this.props.change}
                                        dynamic={false}
                                    />
                                </> : <></>}
                            </FormDataConsumer>
                        </div>
                    </FieldGroup>

                    <FieldGroup
                        label="fieldGroups.projectSections"
                        formClassName={this.props.classes.borderBottom}
                        titleColumn={false}
                        flex={false}
                    >
                        <ArrayInput source="segments">
                            <SimpleFormIterator>
                                <TextInput source="segment" label="resources.actions.fields.name"
                                           validate={this.req}/>
                                <LongTextInput source="description" label="resources.actions.fields.description"/>
                                <SelectInput
                                    source="stage"
                                    choices={this.state.etapasChoices}
                                    label="resources.actions.fields.stage"
                                />
                                <NumberInput source="investment" label="resources.actions.fields.investment"/>
                                <NumberInput source="segment_number" formClassName={this.props.classes.hiddenField}/>
                            </SimpleFormIterator>
                        </ArrayInput>
                    </FieldGroup>

                    <FieldGroup label="fieldGroups.galleryItems" formClassName={this.props.classes.borderBottom}>
                        <FormDataConsumer>
                            {({formData}) => {
                                return (<GalleryItemsInput
                                    value={formData.gallery_items}
                                    change={this.props.change}/>);
                            }}
                        </FormDataConsumer>
                    </FieldGroup>
                </SimpleForm>
            </>);
    }
}

const mapDispatchToProps = {change, submit};

AccionForm.propTypes = {
    classes: PropTypes.shape({
        borderBottom: PropTypes.string.isRequired,
        hiddenField: PropTypes.string.isRequired,
        formCol: PropTypes.string.isRequired
    })
};

export default withRouter(withStyles(styles)(connect(state => ({}), mapDispatchToProps)(translate(AccionForm))));
